import { observer } from 'mobx-react-lite';
import React, { CSSProperties } from 'react';
import { ThemeProvider } from 'styled-components';
import { themeState } from 'styleguide/theme';

/**
 * Used to allow overriding default theme color (primary)
 */
interface ThemeColorOverride {
  dark?: string;
  light?: string;
}

type ThemedIconProps = IconProps & ThemeColorOverride;

export const ThemedIcon = observer((props: ThemedIconProps) => {
  const tint = props.tint ?? (props[themeState.current.name] || themeState.current.primary);

  return (
    <ThemeProvider theme={themeState.current}>
      <Icon tint={tint} {...props} />
    </ThemeProvider>
  );
});

export type ThemedIconType =
  | 'feed'
  | 'dumbbell'
  | 'dumbbell-big'
  | 'profile'
  | 'settings'
  | 'logout'
  | 'profile-large'
  | 'lock'
  | 'pro'
  | 'ruler'
  | 'export'
  | 'moon'
  | 'chevron-down'
  | 'chevron-right'
  | 'chevron-left'
  | 'routine'
  | 'routine-navbar'
  | 'coach-navbar'
  | 'home-navbar'
  | 'exercises-navbar'
  | 'profile-navbar'
  | 'new-folder'
  | 'camera'
  | 'bicep'
  | 'menu'
  | 'micro-dumbbell'
  | 'options'
  | 'back-button'
  | 'link'
  | 'exercise'
  | 'bar-chart'
  | 'reps'
  | 'sets'
  | 'timer'
  | 'developer'
  | 'beginner'
  | 'intermediate'
  | 'advanced'
  | 'muscle'
  | 'scale'
  | 'machine'
  | 'body';

interface IconProps {
  type: ThemedIconType;
  tint?: string;
  style?: CSSProperties;
}

const Icon = (props: IconProps) => {
  switch (props.type) {
    case 'feed':
      return <SvgFeed {...props} />;
    case 'dumbbell':
      return <SvgDumbbell {...props} />;
    case 'dumbbell-big':
      return <SvgDumbbellBig {...props} />;
    case 'profile':
      return <SvgProfile {...props} />;
    case 'settings':
      return <SvgSettings {...props} />;
    case 'logout':
      return <SvgLogout {...props} />;
    case 'profile-large':
      return <SvgProfileLarge {...props} />;
    case 'lock':
      return <SvgLock {...props} />;
    case 'pro':
      return <SvgPro {...props} />;
    case 'ruler':
      return <SvgRuler {...props} />;
    case 'export':
      return <SvgExport {...props} />;
    case 'moon':
      return <SvgMoon {...props} />;
    case 'chevron-down':
      return <SvgChevronDown {...props} />;
    case 'chevron-right':
      return <SvgChevronRight {...props} />;
    case 'chevron-left':
      return <SvgChevronLeft {...props} />;
    case 'routine':
      return <SvgRoutine {...props} />;
    case 'routine-navbar':
      return <SvgRoutineNavBar {...props} />;
    case 'coach-navbar':
      return <SvgCoachNavBar {...props} />;
    case 'home-navbar':
      return <SvgHomeNavBar {...props} />;
    case 'exercises-navbar':
      return <SvgExercisesNavBar {...props} />;
    case 'profile-navbar':
      return <SvgProfileNavBar {...props} />;
    case 'new-folder':
      return <SvgNewFolder {...props} />;
    case 'camera':
      return <SvgCamera {...props} />;
    case 'bicep':
      return <SvgBicep {...props} />;
    case 'micro-dumbbell':
      return <SvgMicroDumbbell {...props} />;
    case 'menu':
      return <SvgMenu {...props} />;
    case 'options':
      return <SvgOptions {...props} />;
    case 'back-button':
      return <SvgBackButton {...props} />;
    case 'link':
      return <SvgLink {...props} />;
    case 'exercise':
      return <SvgExercise {...props} />;
    case 'bar-chart':
      return <SvgBarChart {...props} />;
    case 'reps':
      return <SvgReps {...props} />;
    case 'sets':
      return <SvgSets {...props} />;
    case 'timer':
      return <SvgRestTimer {...props} />;
    case 'developer':
      return <SvgDeveloper {...props} />;
    case 'beginner':
      return <SvgBeginner {...props} />;
    case 'intermediate':
      return <SvgIntermediate {...props} />;
    case 'advanced':
      return <SvgAdvanced {...props} />;
    case 'muscle':
      return <SvgMuscle {...props} />;
    case 'scale':
      return <SvgScale {...props} />;
    case 'machine':
      return <SvgMachine {...props} />;
    case 'body':
      return <SvgBody {...props} />;
  }
};

interface SvgProps {
  tint?: string;
  style?: CSSProperties;
}

function SvgSettings(props: SvgProps) {
  return (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11 2.25C10.6425 2.25 10.3347 2.50237 10.2646 2.85296L9.89444 4.70413C9.65169 4.79019 9.41377 4.88793 9.18173 4.99743L7.61315 3.95171C7.31568 3.7534 6.91959 3.79262 6.6668 4.04542L4.54542 6.1668C4.29262 6.41959 4.2534 6.81568 4.45171 7.11315L5.49751 8.68185C5.38805 8.91394 5.29039 9.15178 5.20433 9.3944L3.35296 9.76456C3.00237 9.83465 2.75 10.1425 2.75 10.5V13.5C2.75 13.8575 3.00237 14.1653 3.35296 14.2354L5.20413 14.6056C5.29019 14.8483 5.38793 15.0862 5.49743 15.3183L4.45171 16.8869C4.2534 17.1843 4.29262 17.5804 4.54542 17.8332L6.66679 19.9546C6.91959 20.2074 7.31568 20.2466 7.61315 20.0483L9.18185 19.0025C9.41394 19.1119 9.65178 19.2096 9.8944 19.2957L10.2646 21.147C10.3347 21.4976 10.6425 21.75 11 21.75H14C14.3575 21.75 14.6653 21.4976 14.7354 21.147L15.1056 19.2959C15.3483 19.2098 15.5862 19.1121 15.8183 19.0026L17.3869 20.0483C17.6843 20.2466 18.0804 20.2074 18.3332 19.9546L20.4546 17.8332C20.7074 17.5804 20.7466 17.1843 20.5483 16.8869L19.5025 15.3182C19.6119 15.0861 19.7096 14.8482 19.7957 14.6056L21.647 14.2354C21.9976 14.1653 22.25 13.8575 22.25 13.5V10.5C22.25 10.1425 21.9976 9.83465 21.647 9.76456L19.7959 9.39444C19.7098 9.15169 19.6121 8.91377 19.5026 8.68173L20.5483 7.11315C20.7466 6.81568 20.7074 6.41959 20.4546 6.16679L18.3332 4.04542C18.0804 3.79262 17.6843 3.7534 17.3869 3.95171L15.8182 4.99751C15.5861 4.88805 15.3482 4.79039 15.1056 4.70433L14.7354 2.85296C14.6653 2.50237 14.3575 2.25 14 2.25H11ZM11.2791 5.42967L11.6149 3.75H13.3851L13.7209 5.42967C13.7757 5.70366 13.9783 5.92449 14.2465 6.00267C14.692 6.13251 15.1144 6.30583 15.5074 6.52208C15.7525 6.65695 16.0523 6.64421 16.285 6.48904L17.7073 5.54085L18.9592 6.79269L18.011 8.21498C17.8557 8.44781 17.843 8.7477 17.9781 8.99282C18.1943 9.38542 18.3678 9.808 18.4972 10.253C18.5752 10.5215 18.7962 10.7243 19.0703 10.7791L20.75 11.1149V12.8851L19.0703 13.2209C18.7963 13.2757 18.5755 13.4783 18.4973 13.7465C18.3675 14.192 18.1942 14.6144 17.9779 15.0074C17.8431 15.2525 17.8558 15.5523 18.011 15.785L18.9592 17.2073L17.7073 18.4592L16.285 17.511C16.0522 17.3557 15.7523 17.343 15.5072 17.4781C15.1146 17.6943 14.692 17.8678 14.247 17.9972C13.9785 18.0752 13.7757 18.2962 13.7209 18.5703L13.3851 20.25H11.6149L11.2791 18.5703C11.2243 18.2963 11.0217 18.0755 10.7535 17.9973C10.308 17.8675 9.88555 17.6942 9.49259 17.4779C9.24751 17.3431 8.94773 17.3558 8.71498 17.511L7.29269 18.4592L6.04085 17.2073L6.98904 15.785C7.14426 15.5522 7.15695 15.2523 7.02195 15.0072C6.80572 14.6146 6.63216 14.192 6.50281 13.747C6.42477 13.4785 6.20385 13.2757 5.92967 13.2209L4.25 12.8851V11.1149L5.92967 10.7791C6.20366 10.7243 6.42449 10.5217 6.50267 10.2535C6.63251 9.80797 6.80583 9.38555 7.02208 8.99259C7.15695 8.74751 7.14421 8.44773 6.98904 8.21498L6.04085 6.79269L7.29269 5.54085L8.71498 6.48904C8.94781 6.64426 9.2477 6.65695 9.49282 6.52195C9.88542 6.30572 10.308 6.13216 10.753 6.00281C11.0215 5.92477 11.2243 5.70385 11.2791 5.42967ZM10.25 12C10.25 10.7575 11.2575 9.75 12.5 9.75C13.7425 9.75 14.75 10.7575 14.75 12C14.75 13.2428 13.7425 14.25 12.5 14.25C11.2575 14.25 10.25 13.2425 10.25 12ZM12.5 8.25C10.4291 8.25 8.75 9.92908 8.75 12C8.75 14.0709 10.4291 15.75 12.5 15.75C14.5709 15.75 16.25 14.0713 16.25 12C16.25 9.92908 14.5709 8.25 12.5 8.25Z"
        fill={props.tint}
      />
    </svg>
  );
}

function SvgLogout(props: SvgProps) {
  return (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M16.5 17L21.5 12M21.5 12L16.5 7M21.5 12H9.5M9.5 3H8.3C6.61984 3 5.77976 3 5.13803 3.32698C4.57354 3.6146 4.1146 4.07354 3.82698 4.63803C3.5 5.27976 3.5 6.11984 3.5 7.8V16.2C3.5 17.8802 3.5 18.7202 3.82698 19.362C4.1146 19.9265 4.57354 20.3854 5.13803 20.673C5.77976 21 6.61984 21 8.3 21H9.5"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgFeed(props: SvgProps) {
  return (
    <svg
      width="30px"
      height="30px"
      viewBox="0 0 30 30"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g stroke={props.tint} strokeWidth={1.2} fill="none" fillRule="evenodd">
        <path d="M10 21.65v-7.026l5.576-5.474 5.576 5.474v7.026h-4.086v-3.536c0-.559-.28-1.08-.745-1.39l-.007-.005a1.323 1.323 0 00-1.473.005c-.46.31-.734.828-.734 1.382v3.544h0H10z" />
        <path d="M8 16.362L15.5 9l7.5 7.362" />
      </g>
    </svg>
  );
}

function SvgDumbbell(props: SvgProps) {
  return (
    <svg
      width="30px"
      height="30px"
      viewBox="0 0 30 30"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g stroke={props.tint} strokeWidth={1.2} fill="none" fillRule="evenodd">
        <path d="M10.891 11a.3.3 0 01.3.3v3.794l7.55.003V11.3a.3.3 0 01.3-.3h2.13a.3.3 0 01.3.3v3.798h.01v-2.46h1.7a.3.3 0 01.3.3v4.312a.3.3 0 01-.3.3h-1.7V15.1h-.01v3.789a.3.3 0 01-.3.3h-2.13a.3.3 0 01-.3-.3v-3.789h-7.55v3.789a.3.3 0 01-.3.3H8.762a.3.3 0 01-.3-.3V17.55h-1.68a.3.3 0 01-.3-.3v-4.312a.3.3 0 01.3-.3l1.68-.001V11.3a.3.3 0 01.3-.3h2.13zM21.471 12.638v4.678M11.191 12.638v4.678M8.462 12.638v4.678M18.741 12.638v4.678" />
      </g>
    </svg>
  );
}

function SvgProfile(props: SvgProps) {
  return (
    <svg
      width="30px"
      height="30px"
      viewBox="0 0 30 30"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g
        transform="translate(9 8)"
        stroke={props.tint}
        strokeWidth={1.2}
        fill="none"
        fillRule="evenodd"
      >
        <path d="M6.24 7.533c1.52 0 2.9.602 3.914 1.58a5.626 5.626 0 011.667 3.243h0l-11.188.206a5.623 5.623 0 011.619-3.377A5.622 5.622 0 016.24 7.533z" />
        <circle cx={6.24} cy={2.773} r={2.773} />
      </g>
    </svg>
  );
}

function SvgProfileLarge(props: SvgProps) {
  return (
    <svg
      width="30px"
      height="30px"
      viewBox="0 0 30 30"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g fill="none" fillRule="evenodd" stroke={props.tint} strokeWidth={1.5}>
        <path d="M15 16.75c2.234 0 4.26.888 5.746 2.33a8.227 8.227 0 012.444 4.916h0l-16.405.242a8.224 8.224 0 012.381-5.072A8.224 8.224 0 0115 16.75z" />
        <circle cx={15} cy={10} r={4} />
      </g>
    </svg>
  );
}

function SvgLock(props: SvgProps) {
  return (
    <svg
      width="30px"
      height="30px"
      viewBox="0 0 30 30"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M21.101 13.198h-1.444v-3.82A4.386 4.386 0 0015.273 5a4.386 4.386 0 00-4.385 4.378v3.82H9.444c-.796 0-1.444.647-1.444 1.442v8.918C8 24.353 8.648 25 9.444 25h11.657c.797 0 1.444-.647 1.444-1.442V14.64c0-.795-.647-1.442-1.444-1.442zm-9.25-3.82a3.423 3.423 0 016.844 0v3.82H11.85v-3.82zm9.732 14.18h0c0 .265-.216.48-.482.48H9.444a.482.482 0 01-.481-.48V14.64c0-.265.216-.48.481-.48h11.657c.266 0 .482.215.482.48v8.918z"
        fill={props.tint}
        fillRule="nonzero"
        stroke={props.tint}
        strokeWidth={0.5}
      />
    </svg>
  );
}

function SvgPro(props: SvgProps) {
  return (
    <svg
      width="30px"
      height="30px"
      viewBox="0 0 30 30"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <text
        fill={props.tint}
        fillRule="evenodd"
        fontFamily="AvenirNext-DemiBold, Avenir Next"
        fontSize={12.756}
        fontWeight={500}
        letterSpacing={-0.138}
      >
        <tspan x={1.889} y={19}>
          {'PRO'}
        </tspan>
      </text>
    </svg>
  );
}

function SvgRuler(props: SvgProps) {
  return (
    <svg
      width="30px"
      height="30px"
      viewBox="0 0 30 30"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g
        transform="rotate(-45 28.692 4.672)"
        stroke={props.tint}
        strokeWidth={1.65}
        fill="none"
        fillRule="evenodd"
      >
        <rect width={22} height={5.5} rx={1.1} />
        <path
          strokeLinecap="round"
          d="M4.499.327v1.729M8.899.327v1.729M13.299.327v1.729M17.699.327v1.729"
        />
      </g>
    </svg>
  );
}

function SvgExport(props: SvgProps) {
  return (
    <svg
      width="30px"
      height="30px"
      viewBox="0 0 30 30"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g fill={props.tint} fillRule="nonzero" stroke={props.tint} strokeWidth={0.65}>
        <path d="M24.524 6h-4.743a.474.474 0 00-.475.472c0 .26.213.471.475.471h3.602l-9.253 9.261a.469.469 0 00.01.66c.184.18.48.182.665.004l9.244-9.25v3.555c0 .261.213.472.475.472a.474.474 0 00.476-.472V6.472A.474.474 0 0024.524 6z" />
        <path d="M21.521 16.883a.479.479 0 00-.479.479v5.244c0 .793-.643 1.435-1.436 1.436H8.394a1.438 1.438 0 01-1.436-1.436V11.394c0-.793.643-1.435 1.436-1.436h5.244a.479.479 0 100-.958H8.394A2.397 2.397 0 006 11.394v11.212A2.397 2.397 0 008.394 25h11.212A2.397 2.397 0 0022 22.606v-5.244a.479.479 0 00-.479-.479z" />
      </g>
    </svg>
  );
}

function SvgMoon(props: SvgProps) {
  return (
    <svg
      width="30px"
      height="30px"
      viewBox="0 0 30 30"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M15.55 4.001c.418-.019.709.167.87.557.172.39.1.733-.214 1.028a7.594 7.594 0 00-1.877 2.59 7.669 7.669 0 00-.65 3.12c0 1.409.348 2.708 1.042 3.897a7.74 7.74 0 002.827 2.827 7.589 7.589 0 003.897 1.042 7.655 7.655 0 003.255-.728c.39-.172.732-.11 1.027.186a.886.886 0 01.25.485c.034.19.012.37-.064.542a10.78 10.78 0 01-4.047 4.632 10.721 10.721 0 01-5.903 1.735c-1.484 0-2.902-.29-4.253-.87-1.352-.582-2.518-1.362-3.498-2.342-.98-.98-1.76-2.146-2.341-3.497C5.29 17.853 5 16.435 5 14.95c0-1.456.274-2.847.82-4.175.548-1.328 1.29-2.477 2.228-3.448.937-.97 2.058-1.753 3.362-2.348a10.91 10.91 0 014.14-.978zm-.054.77c-.46-.342-1.517.138-2.442.414-2.121.634-3.86 1.898-5.205 3.681-1.347 1.786-2.022 3.813-2.022 6.085 0 1.375.27 2.689.81 3.942a10.221 10.221 0 002.16 3.224 10.214 10.214 0 003.224 2.16c1.253.54 2.567.81 3.942.81a9.974 9.974 0 004.334-.975c1.366-.649 2.56-1.514 3.52-2.675.96-1.16 1.672-1.632 1.324-2.164-.349-.533-1.792.298-2.267.43a5.3 5.3 0 01-1.43.186 8.43 8.43 0 01-4.308-1.15 8.504 8.504 0 01-3.134-3.134 8.432 8.432 0 01-1.15-4.309c0-1.636.444-3.157 1.33-4.563.466-.738 1.775-1.622 1.314-1.963z"
        stroke={props.tint}
        strokeWidth={0.6}
        fill={props.tint}
        fillRule="nonzero"
      />
    </svg>
  );
}

function SvgChevronDown(props: SvgProps) {
  return (
    <svg
      width="12"
      height="7"
      viewBox="0 0 12 7"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M2 2L6 6L10 2"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgChevronRight(props: SvgProps) {
  return (
    <svg
      width="8"
      height="14"
      viewBox="0 0 8 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M1.5 12L6.5 7L1.5 2"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgChevronLeft(props: SvgProps) {
  return (
    <svg
      width="8"
      height="14"
      viewBox="0 0 8 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M6.5 2L1.5 7L6.5 12"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgRoutine(props: SvgProps) {
  return (
    <svg
      width="24px"
      height="24px"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M7.998 4h-1a3 3 0 00-3 3v11a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3h-1"
        stroke={props.tint}
        strokeWidth={1.5}
      />
      <path
        d="M16.002 15h-8m8-4h-8"
        stroke={props.tint}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x={8.002} y={2} width={8} height={4} rx={0.5} stroke={props.tint} strokeWidth={1.5} />
    </svg>
  );
}

function SvgRoutineNavBar(props: SvgProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M7.99841 3.9996H6.99841C5.34156 3.9996 3.99841 5.34275 3.99841 6.9996V17.9996C3.99841 19.6565 5.34156 20.9996 6.99841 20.9996H16.9984C18.6553 20.9996 19.9984 19.6565 19.9984 17.9996V6.9996C19.9984 5.34275 18.6553 3.9996 16.9984 3.9996H15.9984"
        stroke={props.tint}
        strokeWidth="1.5"
      />
      <path
        d="M16.0016 15.0004H8.00159M16.0016 11.0004H8.00159"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x="8.00159"
        y="2.0004"
        width="8"
        height="4"
        rx="0.5"
        stroke={props.tint}
        strokeWidth="1.5"
      />
    </svg>
  );
}

function SvgCoachNavBar(props: SvgProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M3.54297 10.648C3.54297 9.10402 4.80129 7.8457 6.34524 7.8457C7.8892 7.8457 9.14752 9.10402 9.14752 10.648C9.14752 12.1919 7.8892 13.4503 6.34524 13.4503C4.80129 13.4503 3.54297 12.1919 3.54297 10.648Z"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
      <path
        d="M1.99609 21.0056L1.99609 16.3808C1.99609 14.9295 3.1695 13.7561 4.62081 13.7561H9.37619C9.84709 13.7561 10.318 13.8642 10.7349 14.0649C10.7349 14.0649 9.91657 13.6712 12.9273 15.1225L14.5175 13.6171C15.0425 13.1076 15.7681 13.1385 16.2622 13.6403C16.7331 14.1267 16.7254 14.8909 16.2391 15.3618L14.0312 17.477C13.6607 17.8399 13.0971 17.9325 12.6262 17.7086C9.24495 16.0798 10.2871 16.5815 10.0864 16.4812V20.9207"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
      <path
        d="M8.15076 5.92245V5.48294C8.15076 4.66371 8.81487 3.9996 9.63409 3.9996H20.5119C21.3311 3.9996 21.9952 4.66372 21.9952 5.48294V15.9762C21.9952 16.7954 21.3312 17.4595 20.512 17.4595C19.2915 17.4595 17.8666 17.4595 16.9958 17.4595"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="square"
      />
    </svg>
  );
}
function SvgHomeNavBar(props: SvgProps) {
  return (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M13.4823 2.50472C13.131 2.22758 12.9553 2.08901 12.7613 2.03574C12.5902 1.98874 12.4098 1.98874 12.2387 2.03574C12.0447 2.08901 11.869 2.22758 11.5177 2.50472L4.73539 7.855C4.28202 8.21264 4.05534 8.39146 3.89203 8.61541C3.74737 8.81378 3.6396 9.03726 3.57403 9.27486C3.5 9.5431 3.5 9.83437 3.5 10.4169V17.7549C3.5 18.891 3.5 19.459 3.71799 19.8929C3.90973 20.2746 4.21569 20.5849 4.59202 20.7794C5.01984 21.0005 5.5799 21.0005 6.7 21.0005H8.7C8.98003 21.0005 9.12004 21.0005 9.227 20.9452C9.32108 20.8966 9.39757 20.819 9.4455 20.7236C9.5 20.6151 9.5 20.4731 9.5 20.1891V14.5093C9.5 13.9413 9.5 13.6573 9.60899 13.4403C9.70487 13.2495 9.85785 13.0943 10.046 12.9971C10.2599 12.8865 10.5399 12.8865 11.1 12.8865H13.9C14.4601 12.8865 14.7401 12.8865 14.954 12.9971C15.1422 13.0943 15.2951 13.2495 15.391 13.4403C15.5 13.6573 15.5 13.9413 15.5 14.5093V20.1891C15.5 20.4731 15.5 20.6151 15.5545 20.7236C15.6024 20.819 15.6789 20.8966 15.773 20.9452C15.88 21.0005 16.02 21.0005 16.3 21.0005H18.3C19.4201 21.0005 19.9802 21.0005 20.408 20.7794C20.7843 20.5849 21.0903 20.2746 21.282 19.8929C21.5 19.459 21.5 18.891 21.5 17.7549V10.4169C21.5 9.83437 21.5 9.5431 21.426 9.27486C21.3604 9.03726 21.2526 8.81378 21.108 8.61541C20.9447 8.39146 20.718 8.21264 20.2646 7.855L13.4823 2.50472Z"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgExercisesNavBar(props: SvgProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M11.5668 3.46056L10.1456 4.88174C9.88397 5.14337 9.89271 5.5763 10.1651 5.84871L18.057 13.7406C18.3294 14.013 18.7623 14.0217 19.0239 13.7601L20.4451 12.3389C20.7067 12.0773 20.698 11.6443 20.4256 11.3719L12.5337 3.48008C12.2613 3.20767 11.8284 3.19893 11.5668 3.46056Z"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.3387 20.4437L13.7599 19.0226C14.0215 18.7609 14.0128 18.328 13.7404 18.0556L5.84856 10.1637C5.57615 9.89133 5.14322 9.8826 4.88159 10.1442L3.46041 11.5654C3.19878 11.827 3.20752 12.26 3.47993 12.5324L11.3718 20.4242C11.6442 20.6966 12.0771 20.7054 12.3387 20.4437Z"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.6172 4.36865L15.3937 2.59218C15.5507 2.43513 15.7533 2.33648 15.9568 2.31793C16.1604 2.29938 16.3482 2.36245 16.4791 2.49326L21.4115 7.42566C21.5423 7.55648 21.6053 7.74433 21.5868 7.94788C21.5682 8.15143 21.4696 8.35401 21.3125 8.51106L19.5361 10.2875"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.2883 19.5356L8.51186 21.3121C8.35481 21.4692 8.15222 21.5678 7.94867 21.5864C7.74512 21.6049 7.55727 21.5418 7.42646 21.411L2.49406 16.4786C2.36324 16.3478 2.30017 16.16 2.31872 15.9564C2.33727 15.7529 2.43593 15.5503 2.59298 15.3932L4.36945 13.6168"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 14L14 10"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgProfileNavBar(props: SvgProps) {
  return (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M12.4979 15C9.32782 15 6.5087 16.5306 4.71389 18.906C4.3276 19.4172 4.13446 19.6728 4.14078 20.0183C4.14566 20.2852 4.31326 20.6219 4.52326 20.7867C4.79508 21 5.17177 21 5.92513 21H19.0707C19.824 21 20.2007 21 20.4725 20.7867C20.6825 20.6219 20.8501 20.2852 20.855 20.0183C20.8613 19.6728 20.6682 19.4172 20.2819 18.906C18.4871 16.5306 15.668 15 12.4979 15Z"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.4979 12C14.9832 12 16.9979 9.98528 16.9979 7.5C16.9979 5.01472 14.9832 3 12.4979 3C10.0126 3 7.9979 5.01472 7.9979 7.5C7.9979 9.98528 10.0126 12 12.4979 12Z"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgNewFolder(props: SvgProps) {
  return (
    <svg
      width="25px"
      height="24px"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M13.5 7l-1.116-2.231c-.32-.642-.481-.963-.72-1.198a2 2 0 00-.748-.462C10.6 3 10.24 3 9.522 3H5.7c-1.12 0-1.68 0-2.108.218a2 2 0 00-.874.874C2.5 4.52 2.5 5.08 2.5 6.2V7m0 0h15.2c1.68 0 2.52 0 3.162.327a3 3 0 011.311 1.311c.327.642.327 1.482.327 3.162v4.4c0 1.68 0 2.52-.327 3.162a3 3 0 01-1.311 1.311C20.22 21 19.38 21 17.7 21H7.3c-1.68 0-2.52 0-3.162-.327a3 3 0 01-1.311-1.311C2.5 18.72 2.5 17.88 2.5 16.2V7zm10 10v-6m-3 3h6"
        stroke={props.tint}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgDumbbellBig(props: SvgProps) {
  return (
    <svg
      width="26px"
      height="26px"
      viewBox="0 0 30 30"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g fill="none" fillRule="evenodd" stroke={props.tint}>
        <rect strokeWidth={1.563} x={22.044} y={7.781} width={3.572} height={13.841} rx={0.893} />
        <rect strokeWidth={1.563} x={4.184} y={7.781} width={3.572} height={13.841} rx={0.893} />
        <rect strokeWidth={1.563} x={25.616} y={10.684} width={2.902} height={7.814} rx={0.893} />
        <rect
          strokeWidth={1.563}
          transform="matrix(-1 0 0 1 5.465 0)"
          x={1.281}
          y={10.684}
          width={2.902}
          height={7.814}
          rx={0.893}
        />
        <path strokeWidth={1.563} d="M25.761 10.081v8.803" />
        <path strokeWidth={1.7} d="M7.733 14.702h14.112" />
      </g>
    </svg>
  );
}

function SvgCamera(props: SvgProps) {
  return (
    <svg
      width="26px"
      height="23px"
      viewBox="0 0 26 23"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M23.568 5.322a2.95 2.95 0 00-2.165-.897h-2.679l-.61-1.626c-.151-.39-.428-.728-.831-1.011-.403-.283-.816-.425-1.238-.425H9.92c-.423 0-.836.142-1.238.425-.403.283-.68.62-.832 1.01l-.61 1.627H4.562c-.845 0-1.567.3-2.165.897A2.95 2.95 0 001.5 7.487v10.717c0 .846.299 1.567.897 2.165a2.95 2.95 0 002.165.898h16.841c.845 0 1.567-.3 2.165-.898a2.95 2.95 0 00.897-2.165V7.487a2.95 2.95 0 00-.897-2.165zm-7.63 10.479c-.853.853-1.88 1.28-3.08 1.28-1.2 0-2.226-.427-3.079-1.28-.853-.853-1.279-1.88-1.279-3.08 0-1.2.427-2.225 1.28-3.078.852-.853 1.879-1.28 3.079-1.28 1.2 0 2.226.427 3.079 1.28.853.852 1.28 1.879 1.28 3.079 0 1.2-.427 2.226-1.28 3.079z"
        fillRule="nonzero"
        stroke={props.tint}
        strokeWidth={1.5}
        fill="none"
      />
    </svg>
  );
}

function SvgBicep(props: SvgProps) {
  return (
    <svg
      width="30px"
      height="30px"
      viewBox="0 0 30 30"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M15.845 7.535l-1.835.917a.335.335 0 00-.158.432l.397.933a.652.652 0 00-.374.63c.015.312.231.561.537.62.187.037.393.061.591.084.374.044.798.093 1.01.226.235.148.518.21.8.223.555.538.753 1.474.677 1.975-.035.23-.099.54-.166.868-.051.247-.106.512-.156.783a3.477 3.477 0 00-3.567.045l-.356.219c-.03-.02-.06-.038-.091-.057a4.714 4.714 0 00-3.368-.643 1.505 1.505 0 00-.173.044 2.999 2.999 0 00-2.083 2.721l-.027.578a3.003 3.003 0 001.81 2.898 4.106 4.106 0 003.64-.21c.514.288 1.35.601 2.61.667 1.715.089 3.764-.306 6.088-1.174a.327.327 0 00.025-.01c.344-.163.584-.461.714-.889.078-.258.11-.547.11-.848 0-.656-.153-1.365-.31-1.92a12.497 12.497 0 00-1.234-2.888 155.988 155.988 0 01-2.587-4.56.335.335 0 00-.096-.11l-2.082-1.525a.333.333 0 00-.346-.029zm1.974 2.05c.229.419 1.5 2.738 2.564 4.518.936 1.564 1.709 3.993 1.368 5.117-.072.237-.186.391-.348.472-4.412 1.644-6.78 1.165-7.851.681.084-.067.17-.132.258-.193a.336.336 0 00.082-.467.333.333 0 00-.465-.082 5.97 5.97 0 00-.442.343 2.432 2.432 0 01-.269.208 3.437 3.437 0 01-3.143.233 2.33 2.33 0 01-1.403-2.25l.027-.579a2.327 2.327 0 011.71-2.138 4.05 4.05 0 012.893.553c.357.223.695.426 1.005.603a.333.333 0 00.499-.291c0-.117-.061-.23-.17-.292-.08-.045-.161-.093-.245-.142l.062-.037a2.809 2.809 0 013.097.103 6.705 6.705 0 00-.076 1.075.334.334 0 10.667-.018c-.019-.692.18-1.652.339-2.423.072-.352.135-.655.173-.904a2.14 2.14 0 00.021-.317c0-.53-.159-1.241-.55-1.824a4.01 4.01 0 00.481-.123.335.335 0 00-.21-.636c-.003.001-.279.092-.618.135-.4.052-.722.016-.908-.101-.34-.213-.821-.269-1.287-.323a7.888 7.888 0 01-.52-.072c.901-.257 2.02-.02 2.03-.018a.335.335 0 00.142-.655 4.857 4.857 0 00-.363-.06l-.883-.057a4.89 4.89 0 00-.576.039l-.32-.752 1.372-.685 1.857 1.36z"
        stroke={props.tint}
        strokeWidth={0.5}
        fill={props.tint}
        fillRule="nonzero"
      />
    </svg>
  );
}

function SvgMicroDumbbell(props: SvgProps) {
  return (
    <svg
      width="30px"
      height="30px"
      viewBox="0 0 30 30"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g transform="translate(7 10)" fill={props.tint} fillRule="evenodd">
        <rect y={3.84} width={16.8} height={1.92} rx={0.9} />
        <rect
          stroke={props.tint}
          strokeWidth={0.9}
          x={2.25}
          y={0.45}
          width={2.1}
          height={8.7}
          rx={0.6}
        />
        <rect
          stroke={props.tint}
          strokeWidth={0.9}
          x={12.45}
          y={0.45}
          width={2.1}
          height={8.7}
          rx={0.6}
        />
        <path
          d="M14.25 2.37h1.55l.505 4.35-2.055.411V2.37zM2.55 2.469V7.23H1L.496 2.88l2.055-.411z"
          stroke={props.tint}
          strokeWidth={0.9}
        />
      </g>
    </svg>
  );
}
function SvgMenu(props: SvgProps) {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M4 16H28M4 8H28M4 24H28"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgOptions(props: SvgProps) {
  return (
    <svg
      width="4"
      height="16"
      viewBox="0 0 4 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M2.00016 7.16675C1.53993 7.16675 1.16683 7.53984 1.16683 8.00008C1.16683 8.46032 1.53993 8.83341 2.00016 8.83341C2.4604 8.83341 2.8335 8.46032 2.8335 8.00008C2.8335 7.53984 2.4604 7.16675 2.00016 7.16675Z"
        stroke={props.tint}
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.00016 13.0001C1.53993 13.0001 1.16683 13.3732 1.16683 13.8334C1.16683 14.2937 1.53993 14.6667 2.00016 14.6667C2.4604 14.6667 2.8335 14.2937 2.8335 13.8334C2.8335 13.3732 2.4604 13.0001 2.00016 13.0001Z"
        stroke={props.tint}
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.00016 1.33341C1.53993 1.33341 1.16683 1.70651 1.16683 2.16675C1.16683 2.62699 1.53993 3.00008 2.00016 3.00008C2.4604 3.00008 2.8335 2.62699 2.8335 2.16675C2.8335 1.70651 2.4604 1.33341 2.00016 1.33341Z"
        stroke={props.tint}
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgBackButton(props: SvgProps) {
  return (
    <svg
      width="18"
      height="16"
      viewBox="0 0 18 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M17 8H1M1 8L7 14M1 8L7 2"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgLink(props: SvgProps) {
  return (
    <svg
      width="21"
      height="20"
      viewBox="0 0 21 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M11.0903 14.3032L9.91177 15.4817C8.28459 17.1089 5.6464 17.1089 4.01922 15.4817C2.39203 13.8546 2.39203 11.2164 4.01922 9.58919L5.19773 8.41068M15.8043 9.58919L16.9828 8.41067C18.61 6.78349 18.61 4.1453 16.9828 2.51812C15.3557 0.890933 12.7175 0.890933 11.0903 2.51812L9.91177 3.69663M7.58436 11.9166L13.4177 6.08325"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgExercise(props: SvgProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M11.5668 3.46056L10.1456 4.88174C9.88397 5.14337 9.89271 5.5763 10.1651 5.84871L18.057 13.7406C18.3294 14.013 18.7623 14.0217 19.0239 13.7601L20.4451 12.3389C20.7067 12.0773 20.698 11.6443 20.4256 11.3719L12.5337 3.48008C12.2613 3.20767 11.8284 3.19893 11.5668 3.46056Z"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.3387 20.4437L13.7599 19.0226C14.0215 18.7609 14.0128 18.328 13.7404 18.0556L5.84856 10.1637C5.57615 9.89133 5.14322 9.8826 4.88159 10.1442L3.46041 11.5654C3.19878 11.827 3.20752 12.26 3.47993 12.5324L11.3718 20.4242C11.6442 20.6966 12.0771 20.7054 12.3387 20.4437Z"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.6172 4.36865L15.3937 2.59218C15.5507 2.43513 15.7533 2.33648 15.9568 2.31793C16.1604 2.29938 16.3482 2.36245 16.4791 2.49326L21.4115 7.42566C21.5423 7.55648 21.6053 7.74433 21.5868 7.94788C21.5682 8.15143 21.4696 8.35401 21.3125 8.51106L19.5361 10.2875"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.2883 19.5356L8.51186 21.3121C8.35481 21.4692 8.15222 21.5678 7.94867 21.5864C7.74512 21.6049 7.55727 21.5418 7.42646 21.411L2.49406 16.4786C2.36324 16.3478 2.30017 16.16 2.31872 15.9564C2.33727 15.7529 2.43593 15.5503 2.59298 15.3932L4.36945 13.6168"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 14L14 10"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgBarChart(props: SvgProps) {
  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M11.25 15H5.75C5.04993 15 4.6999 15 4.43251 15.1362C4.19731 15.2561 4.00608 15.4473 3.88624 15.6825C3.75 15.9499 3.75 16.2999 3.75 17V24.25C3.75 24.9501 3.75 25.3001 3.88624 25.5675C4.00608 25.8027 4.19731 25.9939 4.43251 26.1138C4.6999 26.25 5.04993 26.25 5.75 26.25H11.25M11.25 26.25H18.75M11.25 26.25L11.25 10.75C11.25 10.0499 11.25 9.6999 11.3862 9.43251C11.5061 9.19731 11.6973 9.00608 11.9325 8.88624C12.1999 8.75 12.5499 8.75 13.25 8.75H16.75C17.4501 8.75 17.8001 8.75 18.0675 8.88624C18.3027 9.00608 18.4939 9.19731 18.6138 9.43251C18.75 9.6999 18.75 10.0499 18.75 10.75V26.25M18.75 26.25H24.25C24.9501 26.25 25.3001 26.25 25.5675 26.1138C25.8027 25.9939 25.9939 25.8027 26.1138 25.5675C26.25 25.3001 26.25 24.9501 26.25 24.25V5.75C26.25 5.04993 26.25 4.6999 26.1138 4.43251C25.9939 4.19731 25.8027 4.00608 25.5675 3.88624C25.3001 3.75 24.9501 3.75 24.25 3.75H20.75C20.0499 3.75 19.6999 3.75 19.4325 3.88624C19.1973 4.00608 19.0061 4.19731 18.8862 4.43251C18.75 4.6999 18.75 5.04993 18.75 5.75V10"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgReps(props: SvgProps) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M11.523 5.236H14.4V2.36"
        stroke={props.tint}
        strokeWidth={1.5}
        strokeLinecap="square"
        strokeLinejoin="round"
      />
      <path
        d="M11.955 11.745a6.328 6.328 0 110-8.946L14 4.838"
        stroke={props.tint}
        strokeWidth={1.5}
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgSets(props: SvgProps) {
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M2.666 4h10.667M2.666 12h2.667M2.666 8h6.667"
        stroke={props.tint}
        strokeWidth={1.5}
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgRestTimer(props: SvgProps) {
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.067 1.65h-.75v2.887a.75.75 0 001.5 0v-1.33a4.92 4.92 0 014.166 4.86 4.919 4.919 0 01-4.916 4.916A4.919 4.919 0 013.15 8.067a.75.75 0 00-1.5 0 6.419 6.419 0 006.417 6.416 6.419 6.419 0 006.416-6.416A6.419 6.419 0 008.067 1.65zM4.869 4.481a.75.75 0 00-.937 1.171L7.265 8.32a.75.75 0 10.937-1.171L4.869 4.48z"
        fill={props.tint}
      />
    </svg>
  );
}

function SvgDeveloper(props: SvgProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
      {...props}
    >
      <path
        d="M4.35254 21.4706V7.35294C4.35254 6.05345 5.40599 5 6.70548 5H23.1761C24.4756 5 25.529 6.05345 25.529 7.35294V21.4706"
        stroke={props.tint}
        strokeWidth="1.41176"
        strokeMiterlimit="10"
        strokeLinejoin="round"
      />
      <path
        d="M23.7647 25H6.11765C4.03967 25 2.32603 23.4624 2.04149 21.4688C1.99559 21.1472 2.26336 20.8823 2.58824 20.8823H27.2941C27.619 20.8823 27.8868 21.1472 27.8409 21.4688C27.5561 23.4677 25.8365 25 23.7647 25Z"
        stroke={props.tint}
        strokeWidth="1.41176"
        strokeMiterlimit="10"
        strokeLinejoin="round"
      />
      <path
        d="M19.0586 15.5882L20.8233 13.2353L19.0586 10.8823"
        stroke={props.tint}
        strokeWidth="1.41176"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.8233 15.5882L9.05859 13.2353L10.8233 10.8823"
        stroke={props.tint}
        strokeWidth="1.41176"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.7646 16.7646L16.1176 9.70581"
        stroke={props.tint}
        strokeWidth="1.41176"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgBeginner(props: SvgProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <rect
        x="3"
        y="12"
        width="4"
        height="9"
        rx="1"
        fill={props.tint}
        stroke={props.tint}
        strokeWidth="1.5"
      />
      <rect x="10" y="7" width="4" height="14" rx="1" stroke={props.tint} strokeWidth="1.5" />
      <rect x="17" y="3" width="4" height="18" rx="1" stroke={props.tint} strokeWidth="1.5" />
    </svg>
  );
}

function SvgIntermediate(props: SvgProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <rect
        x="3"
        y="12"
        width="4"
        height="9"
        rx="0.5"
        fill={props.tint}
        stroke={props.tint}
        strokeWidth="1.5"
      />
      <rect
        x="10"
        y="7"
        width="4"
        height="14"
        rx="0.5"
        fill={props.tint}
        stroke={props.tint}
        strokeWidth="1.5"
      />
      <rect x="17" y="3" width="4" height="18" rx="0.5" stroke={props.tint} strokeWidth="1.5" />
    </svg>
  );
}

function SvgAdvanced(props: SvgProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <rect
        x="3"
        y="12"
        width="4"
        height="9"
        rx="0.5"
        fill={props.tint}
        stroke={props.tint}
        strokeWidth="1.5"
      />
      <rect
        x="10"
        y="7"
        width="4"
        height="14"
        rx="0.5"
        fill={props.tint}
        stroke={props.tint}
        strokeWidth="1.5"
      />
      <rect
        x="17"
        y="3"
        width="4"
        height="18"
        rx="0.5"
        fill={props.tint}
        stroke={props.tint}
        strokeWidth="1.5"
      />
    </svg>
  );
}

function SvgMuscle(props: SvgProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M22.0003 22H6.69363C4.66105 20.8333 0.93464 17.6333 2.2897 14.1666C3.64475 10.7 5.67741 6.49996 6.52435 4.83329C7.06133 3.99671 9.51341 2.7065 10.5077 2.2072C10.7715 2.07473 11.0811 2.08553 11.3377 2.23152C11.9533 2.58184 13.1489 3.27065 14.1216 3.88176C14.4794 4.10653 14.6225 4.55246 14.4742 4.94807L13.6981 7.01747C13.516 7.50298 12.9654 7.73811 12.4888 7.53385L11.2464 7.00141C10.7046 6.7692 10.0885 7.1061 9.99156 7.68755L8.81829 14.7272C10.9283 11.9997 16.0911 11.9997 18.3639 14.7272C18.3639 14.7272 19.7276 12.4547 21.5458 12"
        stroke={props.tint}
        strokeWidth="1.36366"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgScale(props: SvgProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M1.51675 5.44502C1.24468 3.63121 2.64946 2 4.48356 2H19.5164C21.3505 2 22.7553 3.63121 22.4832 5.44502L20.3832 19.445C20.163 20.9136 18.9014 22 17.4164 22H6.58356C5.09857 22 3.83704 20.9136 3.61675 19.445L1.51675 5.44502Z"
        stroke={props.tint}
        strokeWidth="1.5"
      />
      <path
        d="M5.85564 7.04988C6.84516 8.36659 7.83469 9.6833 8.82422 11C9.76586 10.2938 10.8871 9.93866 12.0043 9.93866C13.1215 9.93866 14.2427 10.2938 15.1843 11L18.0002 7.04951C14.3533 4.31634 9.50252 4.31671 5.85564 7.04988Z"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 9.79687V7.79688"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgMachine(props: SvgProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M16 19V23M9 23V19"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeMiterlimit="13.33"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 16V4C9 3.44772 9.44772 3 10 3H15C15.5523 3 16 3.44772 16 4V15.9956"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeMiterlimit="13.33"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22 8L19.9527 5H4.05209L2 8"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeMiterlimit="13.33"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 5V1"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeMiterlimit="13.33"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 18C7 16.8954 7.89543 16 9 16H16C17.1046 16 18 16.8954 18 18V19H7V18Z"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M5 23H21"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeMiterlimit="13.33"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SvgBody(props: SvgProps) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M12 7.5H8.76747C8.27339 7.5 7.79679 7.68289 7.42954 8.01341L3.89857 11.1913C3.41013 11.6309 3.39017 12.3902 3.85483 12.8548V12.8548C4.23248 13.2325 4.82082 13.2995 5.27373 13.0164L8.35069 11.0933C8.4165 11.0522 8.50205 11.0984 8.50367 11.176L8.71651 21.3923C8.73512 22.2856 9.46459 23 10.3581 23V23C11.2649 23 12 22.2649 12 21.3581V15.5"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M12 7.5H15.2325C15.7266 7.5 16.2032 7.68289 16.5705 8.01341L20.1014 11.1913C20.5899 11.6309 20.6098 12.3902 20.1452 12.8548V12.8548C19.7675 13.2325 19.1792 13.2995 18.7263 13.0164L15.6493 11.0933C15.5835 11.0522 15.4979 11.0984 15.4963 11.176L15.2835 21.3923C15.2649 22.2856 14.5354 23 13.6419 23V23C12.7351 23 12 22.2649 12 21.3581V15"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M12 5.5C13.2426 5.5 14.25 4.49264 14.25 3.25C14.25 2.00736 13.2426 1 12 1C10.7574 1 9.75 2.00736 9.75 3.25C9.75 4.49264 10.7574 5.5 12 5.5Z"
        stroke={props.tint}
        strokeWidth="1.5"
        strokeMiterlimit="10"
      />
    </svg>
  );
}
